import { Box, Skeleton, Stack, Typography, alpha, Avatar } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LaunchIcon from '@mui/icons-material/Launch';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import { CRM_PALETTE as P } from '../../../theme/brandPalette';
import { useListConversationsQuery } from '../../../store/api/chatApi';

interface Props {
  /** _id de la conversación (24 hex). */
  conversationId: string;
}

/** Preview tapeable de una conversación del chat interno → /chat?conv=<id>. */
export function ChatInlineCard({ conversationId }: Props) {
  const golden = P.brand.golden;
  const myId = useSelector((s: RootState) => s.auth.user?._id);
  const { data: conversations, isLoading } = useListConversationsQuery();
  const to = `/chat?conv=${conversationId}`;

  if (isLoading) {
    return (
      <Box sx={{ my: 1, p: 1.5, border: `1px solid ${alpha(golden, 0.2)}`, borderRadius: 1.5, bgcolor: alpha('#141414', 0.5), minWidth: 'min(260px, 100%)', maxWidth: 340 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}><Skeleton width="60%" /><Skeleton width="80%" /></Box>
        </Stack>
      </Box>
    );
  }

  const conv = conversations?.find((c) => c._id === conversationId);

  // Si no la encontramos (todavía cargando, sin acceso, etc.) → link simple para no perder la referencia.
  if (!conv) {
    return (
      <RouterLink to={to} style={{ color: golden, fontWeight: 600 }}>
        Abrir conversación en el chat interno
      </RouterLink>
    );
  }

  const other = conv.type === 'group' ? null : conv.participants.find((p) => p._id !== myId);
  const title = conv.name?.trim() || (other ? `${other.name} ${other.lastName ?? ''}`.trim() : 'Conversación');
  const avatar = conv.avatar || other?.avatar;
  const initials = title.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
  const last = conv.lastMessage?.text;

  return (
    <Box
      component={RouterLink}
      to={to}
      sx={{
        display: 'block', textDecoration: 'none',
        my: 1, p: 1.5,
        border: `1px solid ${alpha(golden, 0.25)}`,
        borderRadius: 1.5,
        bgcolor: alpha('#141414', 0.7),
        minWidth: 'min(260px, 100%)', maxWidth: 340,
        transition: 'all 0.15s',
        '&:hover': { borderColor: golden, bgcolor: alpha(golden, 0.06), transform: 'translateY(-1px)' },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Avatar src={avatar} sx={{ width: 40, height: 40, bgcolor: alpha(golden, 0.15), color: golden, fontSize: 14, fontWeight: 800 }}>
          {!avatar && initials}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} mb={0.25}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 12, color: golden }} />
            <Typography variant="caption" sx={{ color: golden, fontWeight: 700, fontSize: 10, letterSpacing: '0.04em' }}>
              Chat interno
            </Typography>
            <LaunchIcon sx={{ fontSize: 11, color: alpha(golden, 0.5), ml: 'auto' }} />
          </Stack>
          <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
            {title}
          </Typography>
          {last && (
            <Typography noWrap variant="caption" sx={{ color: 'text.secondary', fontSize: 11, display: 'block' }}>
              {last}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
